import { Injectable, Injector, inject } from '@angular/core';
import { ExternalLogEntryCreateDtoV2 } from 'api-client';

import { CorrelationIdService } from './correlation-id.service';
import { LogBufferService } from './log-buffer.service';
import { DEFAULT_LOG_CONFIG, LOG_CONFIG, LogConfig } from './log-config';
import { LogContext, LogContextService } from './log-context.service';

/**
 * Valid log levels for V2 logging system.
 * Maps to Serilog/Seq log levels on the backend.
 */
export type LogLevelV2 = 'Verbose' | 'Debug' | 'Information' | 'Warning' | 'Error' | 'Fatal';

/**
 * Core logging service that creates structured log entries and sends them via the buffer.
 *
 * This service is the central point for creating V2-compatible log entries with:
 * - Correlation ID tracking
 * - Business context enrichment
 * - Structured properties
 * - Source context and operation names
 */
@Injectable({
  providedIn: 'root',
})
export class LoggingCoreService {
  private _injector = inject(Injector);
  private _bufferService = inject(LogBufferService);
  private _correlationIdService = inject(CorrelationIdService);
  private _contextService = inject(LogContextService);

  private _config: LogConfig | null = null;

  /**
   * Get configuration lazily to allow for injection in any order.
   */
  private getConfig(): LogConfig {
    if (!this._config) {
      try {
        this._config = this._injector.get(LOG_CONFIG) || DEFAULT_LOG_CONFIG;
      } catch {
        this._config = DEFAULT_LOG_CONFIG;
      }
    }
    return this._config;
  }

  /**
   * Create and buffer a structured log entry.
   *
   * @param level Log level (Verbose, Debug, Information, Warning, Error, Fatal)
   * @param message Human-readable log message
   * @param sourceContext Source class/component name (e.g., 'PaymentStepComponent')
   * @param operationName Optional operation name for grouping (e.g., 'ProcessCardPayment')
   * @param properties Additional properties to include in the log
   */
  log(
    level: LogLevelV2,
    message: string,
    sourceContext?: string,
    operationName?: string,
    properties?: Record<string, unknown>,
  ): void {
    const config = this.getConfig();

    // Only proceed if V2 logging is enabled
    if (!config.enableV2Logging) {
      return;
    }

    const context = this._contextService.getContext();

    // Build properties object combining context and custom properties
    const mergedProperties = this.buildProperties(context, properties);

    const entry: ExternalLogEntryCreateDtoV2 = {
      correlationId: this._correlationIdService.getCorrelationId(),
      source: config.source,
      timestamp: new Date().toISOString(),
      level,
      message,
      sourceContext: sourceContext || undefined,
      operationName: operationName || context.operationName || undefined,
      properties: mergedProperties ? JSON.stringify(mergedProperties) : undefined,
    };

    this._bufferService.add(entry);
  }

  /**
   * Log with operation duration tracking.
   * Useful for performance monitoring of specific operations.
   *
   * @param level Log level
   * @param message Log message
   * @param durationMs Duration of the operation in milliseconds
   * @param sourceContext Source class/component name
   * @param operationName Operation name
   * @param properties Additional properties
   */
  logWithDuration(
    level: LogLevelV2,
    message: string,
    durationMs: number,
    sourceContext?: string,
    operationName?: string,
    properties?: Record<string, unknown>,
  ): void {
    const config = this.getConfig();

    if (!config.enableV2Logging) {
      return;
    }

    const context = this._contextService.getContext();
    const mergedProperties = this.buildProperties(context, properties);

    const entry: ExternalLogEntryCreateDtoV2 = {
      correlationId: this._correlationIdService.getCorrelationId(),
      source: config.source,
      timestamp: new Date().toISOString(),
      level,
      message,
      sourceContext: sourceContext || undefined,
      operationName: operationName || context.operationName || undefined,
      operationDurationMs: durationMs,
      properties: mergedProperties ? JSON.stringify(mergedProperties) : undefined,
    };

    this._bufferService.add(entry);
  }

  /**
   * Log an error with exception details.
   *
   * @param message Error message
   * @param error Error object or string
   * @param sourceContext Source class/component name
   * @param operationName Operation name
   * @param properties Additional properties
   */
  logError(
    message: string,
    error: unknown,
    sourceContext?: string,
    operationName?: string,
    properties?: Record<string, unknown>,
  ): void {
    const config = this.getConfig();

    if (!config.enableV2Logging) {
      return;
    }

    const context = this._contextService.getContext();
    const mergedProperties = this.buildProperties(context, properties);

    // Extract exception details
    let exceptionString: string | undefined;
    if (error instanceof Error) {
      exceptionString = `${error.name}: ${error.message}\n${error.stack || ''}`;
    } else if (typeof error === 'string') {
      exceptionString = error;
    } else if (error) {
      try {
        exceptionString = JSON.stringify(error);
      } catch {
        exceptionString = String(error);
      }
    }

    const entry: ExternalLogEntryCreateDtoV2 = {
      correlationId: this._correlationIdService.getCorrelationId(),
      source: config.source,
      timestamp: new Date().toISOString(),
      level: 'Error',
      message,
      sourceContext: sourceContext || undefined,
      operationName: operationName || context.operationName || undefined,
      exception: exceptionString,
      properties: mergedProperties ? JSON.stringify(mergedProperties) : undefined,
    };

    this._bufferService.add(entry);
  }

  /**
   * Check if V2 logging is enabled.
   */
  isV2LoggingEnabled(): boolean {
    return this.getConfig().enableV2Logging;
  }

  /**
   * Build the properties object by merging context and custom properties.
   * Adds URL automatically if in browser environment.
   */
  private buildProperties(
    context: LogContext,
    customProperties?: Record<string, unknown>,
  ): Record<string, unknown> | null {
    const hasContext = Object.keys(context).length > 0;
    const hasCustom = customProperties && Object.keys(customProperties).length > 0;

    if (!hasContext && !hasCustom) {
      // Still add URL if available
      if (typeof window !== 'undefined') {
        return { url: window.location.href };
      }
      return null;
    }

    const properties: Record<string, unknown> = {};

    // Add context properties (excluding operationName which goes in dedicated field)
    if (hasContext) {
      const { operationName: _, ...contextWithoutOpName } = context;
      Object.assign(properties, contextWithoutOpName);
    }

    // Add custom properties
    if (hasCustom) {
      Object.assign(properties, customProperties);
    }

    // Add URL if in browser
    if (typeof window !== 'undefined') {
      properties['url'] = window.location.href;
    }

    return properties;
  }
}
